import { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../services/auth.jsx'
import { api } from '../services/api.js'

const UNITS = ['L', 'mL', 'kg', 'g', 't', 'dose', 'sc']

function fmtDate(iso) {
  if (!iso) return '—'
  const [y, m, d] = iso.split('-')
  return `${d}/${m}/${y}`
}

function fmtNum(n, decimals = 2) {
  if (n == null) return '—'
  return Number(n).toLocaleString('pt-BR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
}

function fmtCurrency(n) {
  if (n == null) return '—'
  return Number(n).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

// ── Styles ────────────────────────────────────────────────────────────────────
const pageWrap = { minHeight: '100vh', background: '#f0fdf4', fontFamily: 'sans-serif' }
const header = { background: '#14532d', color: '#fff', padding: '0 24px', display: 'flex', alignItems: 'center', gap: 16, height: 56, flexShrink: 0 }
const navLink = { color: '#bbf7d0', textDecoration: 'none', fontWeight: 600, fontSize: 14, padding: '6px 12px', borderRadius: 8 }
const content = { maxWidth: 1100, margin: '0 auto', padding: '24px 16px' }
const card = { background: '#fff', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,.08)', padding: 24 }
const greenBtn = { background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 700, fontSize: 13, cursor: 'pointer' }
const grayBtn = { background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: 8, padding: '8px 14px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }
const redBtn = { background: '#ef4444', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 12px', fontWeight: 700, fontSize: 12, cursor: 'pointer' }
const inputStyle = { padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, width: '100%', boxSizing: 'border-box' }
const selectStyle = { ...inputStyle }
const labelStyle = { fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }
const row = { display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }
const formGroup = (w) => ({ flex: w ? `0 0 ${w}` : 1, minWidth: 100 })

function Modal({ title, onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: '100%', maxWidth: 580, maxHeight: '92vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#14532d' }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#6b7280' }}>×</button>
        </div>
        {children}
      </div>
    </div>
  )
}

// ── Preview live do rateio ─────────────────────────────────────────────────────
function RateioPreview({ areaIds, areas, dosePerHa, totalQuantity, totalCost, unit }) {
  const selected = areas.filter(a => areaIds.includes(a.id))
  if (!selected.length || !dosePerHa) return null

  const dose = parseFloat(dosePerHa) || 0
  const qty = totalQuantity ? parseFloat(totalQuantity) : null
  const custo = totalCost ? parseFloat(totalCost) : null

  // Custo por ha derivado da nota fiscal
  const coverageHa = qty && dose ? qty / dose : null
  const costPerHa = custo && coverageHa ? custo / coverageHa : null

  let sumHa = 0, sumVol = 0, sumCost = 0

  return (
    <div style={{ marginTop: 16, border: '1px solid #d1fae5', borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ background: '#f0fdf4', padding: '8px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontWeight: 700, fontSize: 12, color: '#15803d' }}>Prévia do rateio</span>
        {costPerHa != null && (
          <span style={{ fontSize: 12, color: '#15803d' }}>
            {fmtCurrency(costPerHa)}/ha · cobertura {fmtNum(coverageHa)} ha
          </span>
        )}
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: '#f9fafb' }}>
            <th style={{ padding: '6px 12px', textAlign: 'left', color: '#6b7280', fontWeight: 600 }}>Área</th>
            <th style={{ padding: '6px 12px', textAlign: 'right', color: '#6b7280', fontWeight: 600 }}>ha</th>
            <th style={{ padding: '6px 12px', textAlign: 'right', color: '#6b7280', fontWeight: 600 }}>Volume ({unit})</th>
            {costPerHa != null && <th style={{ padding: '6px 12px', textAlign: 'right', color: '#6b7280', fontWeight: 600 }}>Custo</th>}
          </tr>
        </thead>
        <tbody>
          {selected.map(area => {
            const vol = dose * area.ha
            const cost = costPerHa != null ? costPerHa * area.ha : null
            sumHa += area.ha
            sumVol += vol
            if (cost != null) sumCost += cost
            return (
              <tr key={area.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                <td style={{ padding: '6px 12px', color: '#374151' }}>{area.name}</td>
                <td style={{ padding: '6px 12px', textAlign: 'right', color: '#374151' }}>{fmtNum(area.ha)}</td>
                <td style={{ padding: '6px 12px', textAlign: 'right', color: '#374151' }}>{fmtNum(vol)}</td>
                {costPerHa != null && <td style={{ padding: '6px 12px', textAlign: 'right', color: '#374151' }}>{fmtCurrency(cost)}</td>}
              </tr>
            )
          })}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: '2px solid #d1fae5', background: '#f0fdf4' }}>
            <td style={{ padding: '7px 12px', fontWeight: 700, color: '#15803d' }}>Usado nas áreas</td>
            <td style={{ padding: '7px 12px', textAlign: 'right', fontWeight: 700, color: '#15803d' }}>{fmtNum(sumHa)}</td>
            <td style={{ padding: '7px 12px', textAlign: 'right', fontWeight: 700, color: '#15803d' }}>{fmtNum(sumVol)} {unit}</td>
            {costPerHa != null && <td style={{ padding: '7px 12px', textAlign: 'right', fontWeight: 700, color: '#15803d' }}>{fmtCurrency(sumCost)}</td>}
          </tr>
          {qty != null && (() => {
            const saldo = qty - sumVol
            const saldoNeg = saldo < -0.001
            return (
              <tr style={{ background: saldoNeg ? '#fef2f2' : '#f0fdf4' }}>
                <td colSpan={costPerHa != null ? 2 : 2} style={{ padding: '7px 12px', fontWeight: 700, color: saldoNeg ? '#dc2626' : '#6b7280', fontSize: 12 }}>
                  {saldoNeg ? '⚠ Saldo insuficiente' : 'Saldo disponível'}
                </td>
                <td style={{ padding: '7px 12px', textAlign: 'right', fontWeight: 700, color: saldoNeg ? '#dc2626' : '#6b7280', fontSize: 12 }}>
                  {fmtNum(saldo)} {unit}
                </td>
                {costPerHa != null && <td />}
              </tr>
            )
          })()}
        </tfoot>
      </table>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function InsumosPage() {
  const { user, logout, currentFazenda } = useAuth()
  const fazendaId = currentFazenda?.id

  const [insumos, setInsumos] = useState([])
  const [areas, setAreas] = useState([])
  const [activities, setActivities] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // Filters
  const [filters, setFilters] = useState({ date_from: '', date_to: '', area_id: '', activity_key: '', product_name: '' })

  // Form
  const [showForm, setShowForm] = useState(false)
  const [editingInsumo, setEditingInsumo] = useState(null)
  const emptyForm = { date: '', product_name: '', unit: 'L', dose_per_ha: '', total_quantity: '', total_cost: '', activity_key: '', notes: '', area_ids: [] }
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState('')

  // Expanded cards
  const [expandedIds, setExpandedIds] = useState(new Set())

  // Load
  useEffect(() => {
    if (!fazendaId) return
    Promise.all([
      api.getAreas(fazendaId),
      api.getActivities(),
    ])
      .then(([a, act]) => { setAreas(a); setActivities(act.filter(x => x.enabled)) })
      .catch(e => setError(e.message))
  }, [fazendaId])

  const loadInsumos = () => {
    if (!fazendaId) return
    setLoading(true)
    setError('')
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== ''))
    api.getInsumos(params, fazendaId)
      .then(setInsumos)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false))
  }

  useEffect(() => { loadInsumos() }, [fazendaId, filters])

  // Totalizador
  const totals = useMemo(() => {
    let ha = 0, vol = 0, cost = 0, hasCost = false
    insumos.forEach(r => {
      ha += r.total_ha || 0
      vol += r.total_volume_used || 0
      if (r.total_cost_allocated != null) { cost += r.total_cost_allocated; hasCost = true }
    })
    return { ha, vol, cost: hasCost ? cost : null }
  }, [insumos])

  // Form helpers
  function openCreate() {
    setEditingInsumo(null)
    setForm(emptyForm)
    setFormError('')
    setShowForm(true)
  }

  function openEdit(insumo) {
    setEditingInsumo(insumo)
    setForm({
      date: insumo.date,
      product_name: insumo.product_name,
      unit: insumo.unit,
      dose_per_ha: String(insumo.dose_per_ha),
      total_quantity: insumo.total_quantity != null ? String(insumo.total_quantity) : '',
      total_cost: insumo.total_cost != null ? String(insumo.total_cost) : '',
      activity_key: insumo.activity_key || '',
      notes: insumo.notes || '',
      area_ids: insumo.areas.map(a => a.area_id),
    })
    setFormError('')
    setShowForm(true)
  }

  function toggleArea(areaId) {
    setForm(f => ({
      ...f,
      area_ids: f.area_ids.includes(areaId)
        ? f.area_ids.filter(id => id !== areaId)
        : [...f.area_ids, areaId],
    }))
  }

  async function handleSave() {
    if (!form.date || !form.product_name.trim() || !form.dose_per_ha || !form.unit) {
      setFormError('Preencha data, produto, dose e unidade.')
      return
    }
    if (!form.area_ids.length) {
      setFormError('Selecione ao menos uma área.')
      return
    }
    setSaving(true)
    setFormError('')
    const payload = {
      date: form.date,
      product_name: form.product_name.trim(),
      unit: form.unit,
      dose_per_ha: parseFloat(form.dose_per_ha),
      total_quantity: form.total_quantity ? parseFloat(form.total_quantity) : null,
      total_cost: form.total_cost ? parseFloat(form.total_cost) : null,
      activity_key: form.activity_key || null,
      notes: form.notes.trim() || null,
      area_ids: form.area_ids,
    }
    try {
      if (editingInsumo) {
        await api.updateInsumo(editingInsumo.id, payload, fazendaId)
      } else {
        await api.createInsumo(payload, fazendaId)
      }
      setShowForm(false)
      loadInsumos()
    } catch (e) {
      setFormError(e.message)
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Excluir este lançamento?')) return
    try {
      await api.deleteInsumo(id, fazendaId)
      loadInsumos()
    } catch (e) {
      alert(e.message)
    }
  }

  function toggleExpand(id) {
    setExpandedIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  function setFilter(key, val) {
    setFilters(f => ({ ...f, [key]: val }))
  }

  function clearFilters() {
    setFilters({ date_from: '', date_to: '', area_id: '', activity_key: '', product_name: '' })
  }

  const activityLabel = (key) => activities.find(a => a.key === key)?.label || key

  return (
    <div style={pageWrap}>
      {/* Header */}
      <div style={header}>
        <Link to="/" style={navLink}>← Início</Link>
        <span style={{ fontWeight: 800, fontSize: 16 }}>🌿 Insumos</span>
        {currentFazenda && (
          <span style={{ fontSize: 13, color: '#bbf7d0', marginLeft: 4 }}>{currentFazenda.name}</span>
        )}
        <div style={{ flex: 1 }} />
        <button onClick={logout} style={{ background: 'none', border: '1px solid rgba(255,255,255,.3)', color: '#fff', borderRadius: 8, padding: '6px 14px', fontSize: 13, cursor: 'pointer' }}>
          Sair
        </button>
      </div>

      <div style={content}>
        {error && <div style={{ background: '#fee2e2', color: '#991b1b', borderRadius: 8, padding: '10px 16px', marginBottom: 16 }}>{error}</div>}

        {/* Filtros */}
        <div style={{ ...card, marginBottom: 20 }}>
          <div style={{ ...row, marginBottom: 0 }}>
            <div style={formGroup('140px')}>
              <label style={labelStyle}>De</label>
              <input type="date" style={inputStyle} value={filters.date_from} onChange={e => setFilter('date_from', e.target.value)} />
            </div>
            <div style={formGroup('140px')}>
              <label style={labelStyle}>Até</label>
              <input type="date" style={inputStyle} value={filters.date_to} onChange={e => setFilter('date_to', e.target.value)} />
            </div>
            <div style={formGroup('160px')}>
              <label style={labelStyle}>Área</label>
              <select style={selectStyle} value={filters.area_id} onChange={e => setFilter('area_id', e.target.value)}>
                <option value="">Todas</option>
                {areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
            <div style={formGroup('180px')}>
              <label style={labelStyle}>Manejo</label>
              <select style={selectStyle} value={filters.activity_key} onChange={e => setFilter('activity_key', e.target.value)}>
                <option value="">Todos</option>
                {activities.map(a => <option key={a.key} value={a.key}>{a.label}</option>)}
              </select>
            </div>
            <div style={formGroup()}>
              <label style={labelStyle}>Produto</label>
              <input style={inputStyle} placeholder="Buscar produto..." value={filters.product_name} onChange={e => setFilter('product_name', e.target.value)} />
            </div>
            <div style={{ alignSelf: 'flex-end' }}>
              <button onClick={clearFilters} style={grayBtn}>Limpar</button>
            </div>
          </div>
        </div>

        {/* Totalizador + botão novo */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ background: '#dcfce7', borderRadius: 10, padding: '10px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#15803d', marginBottom: 2 }}>Total ha</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#14532d' }}>{fmtNum(totals.ha)}</div>
            </div>
            <div style={{ background: '#dbeafe', borderRadius: 10, padding: '10px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#1d4ed8', marginBottom: 2 }}>Total volume</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#1e40af' }}>{fmtNum(totals.vol)}</div>
            </div>
            {totals.cost != null && (
              <div style={{ background: '#fef9c3', borderRadius: 10, padding: '10px 20px', textAlign: 'center' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#854d0e', marginBottom: 2 }}>Total custo</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#713f12' }}>{fmtCurrency(totals.cost)}</div>
              </div>
            )}
          </div>
          <button onClick={openCreate} style={greenBtn}>+ Novo Lançamento</button>
        </div>

        {/* Lista */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>Carregando...</div>
        ) : insumos.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
            Nenhum lançamento encontrado. Clique em "+ Novo Lançamento" para começar.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {insumos.map(insumo => {
              const expanded = expandedIds.has(insumo.id)
              return (
                <div key={insumo.id} style={card}>
                  {/* Linha principal */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                    <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 13, color: '#6b7280', minWidth: 80 }}>{fmtDate(insumo.date)}</span>
                      <span style={{ fontWeight: 700, color: '#14532d', fontSize: 15 }}>{insumo.product_name}</span>
                      <span style={{ background: '#f0fdf4', border: '1px solid #d1fae5', borderRadius: 6, padding: '2px 10px', fontSize: 12, color: '#15803d', fontWeight: 600 }}>
                        {fmtNum(insumo.dose_per_ha, 2)} {insumo.unit}/ha
                      </span>
                      {insumo.activity_key && (
                        <span style={{ background: '#ede9fe', border: '1px solid #ddd6fe', borderRadius: 6, padding: '2px 10px', fontSize: 12, color: '#5b21b6', fontWeight: 600 }}>
                          {activityLabel(insumo.activity_key)}
                        </span>
                      )}
                      <span style={{ fontSize: 12, color: '#9ca3af' }}>
                        {insumo.areas.length} área{insumo.areas.length !== 1 ? 's' : ''} · {fmtNum(insumo.total_ha, 2)} ha
                      </span>
                      {insumo.cost_per_ha != null && (
                        <span style={{ background: '#fef9c3', borderRadius: 6, padding: '2px 8px', fontSize: 12, color: '#713f12', fontWeight: 600 }}>
                          {fmtCurrency(insumo.cost_per_ha)}/ha
                        </span>
                      )}
                      {insumo.total_cost_allocated != null && (
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#15803d' }}>{fmtCurrency(insumo.total_cost_allocated)} total</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => toggleExpand(insumo.id)} style={{ ...grayBtn, padding: '4px 10px', fontSize: 12 }}>
                        {expanded ? '▲ Fechar' : '▼ Detalhar'}
                      </button>
                      <button onClick={() => openEdit(insumo)} style={{ ...grayBtn, padding: '4px 10px', fontSize: 12 }}>Editar</button>
                      <button onClick={() => handleDelete(insumo.id)} style={redBtn}>Excluir</button>
                    </div>
                  </div>

                  {/* Notas */}
                  {insumo.notes && (
                    <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280', fontStyle: 'italic' }}>{insumo.notes}</div>
                  )}

                  {/* Rateio expandido */}
                  {expanded && (
                    <div style={{ marginTop: 14, border: '1px solid #d1fae5', borderRadius: 8, overflow: 'hidden' }}>
                      <div style={{ background: '#f0fdf4', padding: '8px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: 700, fontSize: 12, color: '#15803d' }}>Rateio por área</span>
                        <span style={{ fontSize: 12, color: '#15803d' }}>
                          {insumo.cost_per_ha != null && `${fmtCurrency(insumo.cost_per_ha)}/ha · `}
                          {insumo.coverage_ha != null && `cobertura ${fmtNum(insumo.coverage_ha)} ha`}
                        </span>
                      </div>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr style={{ background: '#f9fafb' }}>
                            <th style={{ padding: '7px 14px', textAlign: 'left', color: '#6b7280', fontWeight: 600 }}>Área</th>
                            <th style={{ padding: '7px 14px', textAlign: 'right', color: '#6b7280', fontWeight: 600 }}>ha</th>
                            <th style={{ padding: '7px 14px', textAlign: 'right', color: '#6b7280', fontWeight: 600 }}>Volume ({insumo.unit})</th>
                            {insumo.cost_per_ha != null && (
                              <th style={{ padding: '7px 14px', textAlign: 'right', color: '#6b7280', fontWeight: 600 }}>Custo</th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {insumo.areas.map(a => (
                            <tr key={a.area_id} style={{ borderTop: '1px solid #f3f4f6' }}>
                              <td style={{ padding: '7px 14px', color: '#374151' }}>{a.area_name}</td>
                              <td style={{ padding: '7px 14px', textAlign: 'right', color: '#374151' }}>{fmtNum(a.ha)}</td>
                              <td style={{ padding: '7px 14px', textAlign: 'right', color: '#374151' }}>{fmtNum(a.volume_applied)}</td>
                              {insumo.cost_per_ha != null && (
                                <td style={{ padding: '7px 14px', textAlign: 'right', color: '#374151' }}>{fmtCurrency(a.cost)}</td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr style={{ borderTop: '2px solid #d1fae5', background: '#f0fdf4' }}>
                            <td style={{ padding: '8px 14px', fontWeight: 700, color: '#15803d' }}>Usado nas áreas</td>
                            <td style={{ padding: '8px 14px', textAlign: 'right', fontWeight: 700, color: '#15803d' }}>{fmtNum(insumo.total_ha)}</td>
                            <td style={{ padding: '8px 14px', textAlign: 'right', fontWeight: 700, color: '#15803d' }}>{fmtNum(insumo.total_volume_used)} {insumo.unit}</td>
                            {insumo.cost_per_ha != null && (
                              <td style={{ padding: '8px 14px', textAlign: 'right', fontWeight: 700, color: '#15803d' }}>{fmtCurrency(insumo.total_cost_allocated)}</td>
                            )}
                          </tr>
                          {insumo.total_quantity != null && (() => {
                            const saldo = insumo.total_quantity - insumo.total_volume_used
                            const neg = saldo < -0.001
                            return (
                              <tr style={{ background: neg ? '#fef2f2' : '#f0fdf4' }}>
                                <td colSpan={2} style={{ padding: '7px 14px', fontWeight: 700, color: neg ? '#dc2626' : '#6b7280', fontSize: 12 }}>
                                  {neg ? '⚠ Saldo insuficiente' : 'Saldo disponível'}
                                </td>
                                <td style={{ padding: '7px 14px', textAlign: 'right', fontWeight: 700, color: neg ? '#dc2626' : '#6b7280', fontSize: 12 }}>
                                  {fmtNum(saldo)} {insumo.unit}
                                </td>
                                {insumo.cost_per_ha != null && <td />}
                              </tr>
                            )
                          })()}
                        </tfoot>
                      </table>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Modal de criação/edição */}
      {showForm && (
        <Modal title={editingInsumo ? 'Editar Lançamento' : 'Novo Lançamento'} onClose={() => setShowForm(false)}>
          {/* Linha 1: Data + Produto */}
          <div style={{ ...row, marginBottom: 14 }}>
            <div style={formGroup('140px')}>
              <label style={labelStyle}>Data *</label>
              <input type="date" style={inputStyle} value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
            </div>
            <div style={formGroup()}>
              <label style={labelStyle}>Produto *</label>
              <input style={inputStyle} placeholder="ex: Zapp (glifosato)" value={form.product_name} onChange={e => setForm(f => ({ ...f, product_name: e.target.value }))} />
            </div>
          </div>

          {/* Linha 2: Dose + Unidade + Manejo */}
          <div style={{ ...row, marginBottom: 14 }}>
            <div style={formGroup('120px')}>
              <label style={labelStyle}>Dose/ha *</label>
              <input type="number" step="0.01" min="0" style={inputStyle} placeholder="ex: 2" value={form.dose_per_ha} onChange={e => setForm(f => ({ ...f, dose_per_ha: e.target.value }))} />
            </div>
            <div style={formGroup('100px')}>
              <label style={labelStyle}>Unidade *</label>
              <select style={selectStyle} value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}>
                {UNITS.map(u => <option key={u} value={u}>{u}/ha</option>)}
              </select>
            </div>
            <div style={formGroup()}>
              <label style={labelStyle}>Manejo</label>
              <select style={selectStyle} value={form.activity_key} onChange={e => setForm(f => ({ ...f, activity_key: e.target.value }))}>
                <option value="">— nenhum —</option>
                {activities.map(a => <option key={a.key} value={a.key}>{a.label}</option>)}
              </select>
            </div>
          </div>

          {/* Linha 3: Nota fiscal (quantidade + custo total) */}
          <div style={{ ...row, marginBottom: 14, background: '#f9fafb', borderRadius: 8, padding: '10px 12px' }}>
            <div style={{ flex: '0 0 100%', marginBottom: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Nota fiscal (opcional)</span>
            </div>
            <div style={formGroup('160px')}>
              <label style={labelStyle}>Quantidade total ({form.unit})</label>
              <input type="number" step="0.01" min="0" style={inputStyle} placeholder="ex: 500" value={form.total_quantity} onChange={e => setForm(f => ({ ...f, total_quantity: e.target.value }))} />
            </div>
            <div style={formGroup('160px')}>
              <label style={labelStyle}>Custo total (R$)</label>
              <input type="number" step="0.01" min="0" style={inputStyle} placeholder="ex: 9000" value={form.total_cost} onChange={e => setForm(f => ({ ...f, total_cost: e.target.value }))} />
            </div>
            <div style={{ flex: 1, alignSelf: 'flex-end', fontSize: 12, color: '#9ca3af', paddingBottom: 4 }}>
              {form.total_quantity && form.dose_per_ha
                ? `Cobertura: ${fmtNum(parseFloat(form.total_quantity) / parseFloat(form.dose_per_ha))} ha`
                : 'Informe quantidade e dose para ver a cobertura'}
            </div>
          </div>

          {/* Seleção de áreas */}
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Áreas *</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '8px 0' }}>
              {areas.map(area => {
                const checked = form.area_ids.includes(area.id)
                return (
                  <button
                    key={area.id}
                    type="button"
                    onClick={() => toggleArea(area.id)}
                    style={{
                      padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                      border: checked ? '2px solid #16a34a' : '2px solid #e5e7eb',
                      background: checked ? '#dcfce7' : '#f9fafb',
                      color: checked ? '#15803d' : '#374151',
                    }}
                  >
                    {area.name} ({fmtNum(area.ha)} ha)
                  </button>
                )
              })}
            </div>
          </div>

          {/* Preview live */}
          <RateioPreview
            areaIds={form.area_ids}
            areas={areas}
            dosePerHa={form.dose_per_ha}
            totalQuantity={form.total_quantity}
            totalCost={form.total_cost}
            unit={form.unit}
          />

          {/* Observações */}
          <div style={{ marginTop: 14, marginBottom: 14 }}>
            <label style={labelStyle}>Observações</label>
            <textarea style={{ ...inputStyle, height: 60, resize: 'vertical' }} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </div>

          {formError && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 10 }}>{formError}</div>}

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button onClick={() => setShowForm(false)} style={grayBtn}>Cancelar</button>
            <button onClick={handleSave} style={greenBtn} disabled={saving}>
              {saving ? 'Salvando...' : editingInsumo ? 'Salvar alterações' : 'Lançar'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  )
}
